import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  MessageSquare,
  Clock,
  User,
  Calendar,
  ExternalLink,
  Download,
  Share2,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Target,
  Zap,
  XCircle,
  Settings
} from 'lucide-react';
import { format } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import {getPiiData, getPiiChatUserInformation, type PiiData, createShareableChatUrl} from '@/utils/piiUtils';
import { processActionButtons } from '@/utils/aiUtils';

interface ConversationDetailModalProps {
  conversationId: string | null;
  isOpen: boolean;
  onClose: () => void;
}

interface Message {
  id: string;
  content: string;
  type: string;
  created_at: string;
  question_id?: string;
  metadata?: any;
}

interface UserResponse {
  id: string;
  question_id: string;
  response_text?: string;
  response_options?: string[];
  created_at: string;
  questions?: {
    id: string;
    text: string;
    shortname?: string;
  };
}

interface TriggerLog {
  id: string;
  trigger_rule_id: string;
  conversation_id: string;
  payload: any;
  response?: any;
  status: string;
  error_message?: string;
  executed_at: string;
  execution_time_ms?: number;
  response_status?: number;
  response_data?: any;
  chatbot_id?: string;
  session_id?: string;
  trigger_name?: string;
  webhook_url?: string;
  timestamp?: string;
  pii_data?: any;
  trigger_rules?: {
    id: string;
    name: string;
    webhookurl?: string;
  };
}

interface ConversationDetail {
  id: string;
  chatbot_id: string;
  session_id?: string;
  status: string;
  metadata: any;
  created_at: string;
  updated_at: string;
  messages: Message[];
  user_responses: UserResponse[];
  trigger_logs: TriggerLog[];
  chatbot?: {
    id: string;
    settings?: any;
  };
}

export const ConversationDetailModal: React.FC<ConversationDetailModalProps> = ({
  conversationId,
  isOpen,
  onClose
}) => {
  const [conversation, setConversation] = useState<ConversationDetail | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [piiData, setPiiData] = useState<PiiData | null>(null);
  const [userData, setUserData] = useState<{ fields: JSON } | null>(null);
  const [piiToken, setPiiToken] = useState<string | null>(null);
  const [isPiiLoading, setIsPiiLoading] = useState(false);
  const { toast } = useToast();

  const fetchPiiData = async (token: string) => {
    if (!token) return;

    setIsPiiLoading(true);
    try {
      const data = await getPiiData(token);
      setPiiData(data);
      if (data._chat_references?.personality_id) {
        const { success, data: user } = await getPiiChatUserInformation(conversationId, token);
        if (success) setUserData(user)
      }
    } catch (err) {
      console.warn('Failed to fetch PII data:', err);
      setPiiData(null);
    } finally {
      setIsPiiLoading(false);
    }
  };

  const fetchConversationDetail = async () => {
    if (!conversationId) return;

    setIsLoading(true);
    setError(null);

    try {
      // Fetch conversation with messages and user responses
      const { data: conversationData, error: convError } = await supabase
        .from('conversations')
        .select(`
          *,
          chatbot:chatbot_id (
            id,
            settings
          )
        `)
        .eq('id', conversationId)
        .single();

      if (convError) throw convError;

      // Fetch messages for this conversation
      const { data: messagesData, error: messagesError } = await supabase
        .from('messages')
        .select('*')
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: true });

      if (messagesError) throw messagesError;

      // Fetch user responses for this conversation with question details
      const { data: responsesData, error: responsesError } = await supabase
        .from('user_responses')
        .select(`
          *,
          questions:question_id (
            id,
            text,
            shortname
          )
        `)
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: true });

      if (responsesError) throw responsesError;

      // Fetch trigger logs for this conversation
      const { data: triggerLogsData, error: triggerLogsError } = await supabase
        .from('trigger_logs')
        .select(`
          *,
          trigger_rules:trigger_rule_id (
            id,
            name,
            webhookurl
          )
        `)
        .eq('conversation_id', conversationId)
        .order('executed_at', { ascending: true });

      if (triggerLogsError) {
        console.warn('Failed to fetch trigger logs:', triggerLogsError);
        // Don't throw error, just continue without trigger logs
      }

      // Debug log to check metadata
      console.log('🔍 ConversationDetailModal - Conversation data:', {
        conversationId,
        hasMetadata: !!conversationData.metadata,
        metadataKeys: conversationData.metadata ? Object.keys(conversationData.metadata) : [],
        hasAiExtractedData: !!conversationData.metadata?.ai_extracted_data,
        aiExtractedData: conversationData.metadata?.ai_extracted_data,
        fullMetadata: conversationData.metadata
      });

      setConversation({
        ...conversationData,
        messages: messagesData || [],
        user_responses: responsesData || [],
        trigger_logs: triggerLogsData || []
      });

      // Check for PII token in conversation metadata
      const metadata = conversationData.metadata as any;
      const piiTokenFromMetadata = metadata?.pii_token || metadata?.piiToken;

      if (piiTokenFromMetadata) {
        setPiiToken(piiTokenFromMetadata);
        await fetchPiiData(piiTokenFromMetadata);
      } else {
        // Check for auto-created PII token using session ID
        const sessionId = conversationData.session_id;
        if (sessionId) {
          // If no session storage token, try to find PII data by searching for session metadata
          const { data: piiTokensData, error: piiError } = await supabase
            .from('pii_tokens')
            .select(`
              token,
              pii_data!inner(
                data
              )
            `)
            .contains('pii_data.data', { _session_id: sessionId })
            .order('created_at', { ascending: false })
            .limit(1);

          if (!piiError && piiTokensData && piiTokensData.length > 0) {
            const foundToken = piiTokensData[0].token;
            setPiiToken(foundToken);
            await fetchPiiData(foundToken);
          } else {
            // Clear PII data if no token found
            setPiiData(null);
            setPiiToken(null);
          }
        } else {
          // Clear PII data if no session ID
          setPiiData(null);
          setPiiToken(null);
        }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch conversation details';
      setError(errorMessage);
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && conversationId) {
      fetchConversationDetail();
    }
  }, [isOpen, conversationId]);

  const exportConversation = () => {
    if (!conversation) return;

    const exportData = {
      conversation_id: conversation.id,
      status: conversation.status,
      created_at: conversation.created_at,
      metadata: conversation.metadata,
      messages: conversation.messages.map(msg => ({
        content: msg.content,
        sender: msg.type === 'bot' || msg.type === 'system' || msg.type === 'ai' ? 'bot' : 'user',
        timestamp: msg.created_at,
        type: msg.type
      })),
      user_responses: conversation.user_responses.map(resp => ({
        question_id: resp.question_id,
        question_text: resp.questions?.text || 'Question not found',
        question_shortname: resp.questions?.shortname || null,
        response_text: resp.response_text,
        response_options: resp.response_options,
        timestamp: resp.created_at
      }))
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: 'application/json'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `conversation-${conversation.id}-${format(new Date(), 'yyyy-MM-dd')}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const shareConversation = async () => {
    if (!conversation || !piiToken) {
      toast({
        title: "Cannot share conversation",
        description: "No PII token available for this conversation.",
        variant: "destructive",
      });
      return;
    }

    try {
      const shareableUrl = createShareableChatUrl(conversation.chatbot_id, conversation.session_id);

      if (!shareableUrl) {
        toast({
          title: "Cannot create shareable link",
          description: "Unable to generate a shareable URL for this conversation.",
          variant: "destructive",
        });
        return;
      }

      await navigator.clipboard.writeText(shareableUrl);

      toast({
        title: "Shareable link copied!",
        description: "The conversation link has been copied to your clipboard. Users can continue this chat with all data intact.",
      });
    } catch (error) {
      toast({
        title: "Failed to copy link",
        description: "Please copy the link manually from the PII section below.",
        variant: "destructive",
      });
    }
  };

  const getStatusBadge = () => {
    const isCompleted = conversation?.metadata?.completed_at || conversation?.metadata?.completed;

    if (isCompleted) {
      return (
        <Badge variant="default" className="bg-green-100 text-green-800">
          <CheckCircle className="h-3 w-3 mr-1" />
          Completed
        </Badge>
      );
    }

    return (
      <Badge variant="secondary" className="bg-amber-100 text-amber-800">
        <AlertCircle className="h-3 w-3 mr-1" />
        In Progress
      </Badge>
    );
  };

  const formatMetadata = (metadata: any) => {
    if (!metadata) return {};

    return {
      'UTM Source': metadata.utm_source || 'Direct',
      'UTM Medium': metadata.utm_medium || 'N/A',
      'UTM Campaign': metadata.utm_campaign || 'N/A',
      'Referrer': metadata.referrer || 'Direct',
      'User Agent': metadata.userAgent ?
        metadata.userAgent.split(' ').slice(0, 3).join(' ') + '...' : 'N/A',
      'Starting URL': metadata.startingUrl || 'N/A'
    };
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[95vh] overflow-hidden">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Conversation Details
            </DialogTitle>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={fetchConversationDetail}
                disabled={isLoading}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              {piiToken && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={shareConversation}
                  disabled={!conversation || !piiToken}
                >
                  <Share2 className="h-4 w-4 mr-2" />
                  Share
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={exportConversation}
                disabled={!conversation}
              >
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </div>
          </div>
        </DialogHeader>

        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="h-6 w-6 animate-spin mr-2" />
            <span>Loading conversation details...</span>
          </div>
        )}

        {error && (
          <div className="text-center py-8 text-red-600">
            <AlertCircle className="h-8 w-8 mx-auto mb-2" />
            <p>{error}</p>
          </div>
        )}

        {conversation && !isLoading && (
          <Tabs defaultValue="conversation" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="conversation" className="flex items-center gap-2">
                <MessageSquare className="h-4 w-4" />
                Conversation
              </TabsTrigger>
              <TabsTrigger value="details" className="flex items-center gap-2">
                <Settings className="h-4 w-4" />
                Details
              </TabsTrigger>
            </TabsList>

            {/* Conversation Tab */}
            <TabsContent value="conversation" className="mt-6">
              <ScrollArea className="h-[calc(95vh-200px)]">
                <div className="space-y-4 pr-4">
              {/* Messages and Responses */}
            <div className={`grid grid-cols-1 ${(() => {
              const settings = typeof conversation.chatbot?.settings === 'string' 
                ? JSON.parse(conversation.chatbot.settings) 
                : conversation.chatbot?.settings;
              return settings?.chatMode !== 'ai_only';
            })() ? 'lg:grid-cols-2' : ''} gap-4`}>
              {/* Messages */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Messages</CardTitle>
                  <div className="text-xs text-muted-foreground mt-2 space-y-1">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-green-50 border border-green-200 rounded"></div>
                      <span>🤖 AI Responses</span>
                      <div className="w-3 h-3 bg-purple-500 rounded ml-4"></div>
                      <span>💬 User Chat</span>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <ScrollArea className="h-[400px] px-4">
                    <div className="space-y-3 py-2">
                      {conversation.messages.map((message, index) => {
                        // Check if message is from bot using type field or metadata
                        const isBot = message.type === 'bot' || message.type === 'system' || message.type === 'ai' ||
                                    (message.metadata && message.metadata.is_bot === true);

                        // Check if this is a structured question (has questionId in metadata)
                        const isStructuredQuestion = isBot && (message.metadata?.question_id || message.metadata?.questionId);

                        // Check if this is an AI conversation message (no question_id or questionId)
                        const isAIConversation = !message.metadata?.question_id && !message.metadata?.questionId;

                        // Determine if this is a user message in AI conversation
                        const isUserAIMessage = !isBot && isAIConversation;

                        return (
                          <div key={message.id} className="space-y-1">
                            <div className={`flex ${isBot ? 'justify-start' : 'justify-end'}`}>
                              <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
                                isBot
                                  ? isStructuredQuestion
                                    ? 'bg-blue-50 text-blue-900 border border-blue-200' // Structured questions
                                    : 'bg-green-50 text-green-900 border border-green-200' // AI responses
                                  : isUserAIMessage
                                    ? 'bg-purple-500 text-white' // AI conversation user messages
                                    : 'bg-primary text-primary-foreground' // Structured flow user responses
                              }`}>
                                <div className="space-y-1">
                                  {isStructuredQuestion && (
                                    <div className="text-xs font-medium text-blue-600 mb-1">
                                      📋 Question
                                    </div>
                                  )}
                                  {isBot && isAIConversation && (
                                    <div className="text-xs font-medium text-green-600 mb-1">
                                      🤖 AI Response
                                    </div>
                                  )}
                                  {isUserAIMessage && (
                                    <div className="text-xs font-medium text-purple-200 mb-1">
                                      💬 Chat Message
                                    </div>
                                  )}
                                  <div dangerouslySetInnerHTML={{ __html: processActionButtons(message.content) }} />
                                </div>
                              </div>
                            </div>
                            <p className={`text-xs text-muted-foreground ${
                              isBot ? 'text-left' : 'text-right'
                            }`}>
                              {format(new Date(message.created_at), 'HH:mm:ss')}
                              {isStructuredQuestion && (
                                <span className="ml-2 text-blue-500">• Structured Question</span>
                              )}
                              {isBot && isAIConversation && (
                                <span className="ml-2 text-green-500">• AI Response</span>
                              )}
                              {isUserAIMessage && (
                                <span className="ml-2 text-purple-500">• User Chat</span>
                              )}
                            </p>
                            {/* {index < conversation.messages.length - 1 && (
                              <Separator className="my-2" />
                            )} */}
                          </div>
                        );
                      })}
                      {conversation.messages.length === 0 && (
                        <p className="text-center text-muted-foreground py-4">
                          No messages found
                        </p>
                      )}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>

              {/* User Responses - Only show if not in AI-only mode */}
              {(() => {
                const settings = typeof conversation.chatbot?.settings === 'string' 
                  ? JSON.parse(conversation.chatbot.settings) 
                  : conversation.chatbot?.settings;
                return settings?.chatMode !== 'ai_only';
              })() && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">User Responses</CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <ScrollArea className="h-[400px] px-4">
                      <div className="space-y-3 py-2">
                        {conversation.user_responses.map((response, index) => (
                        <div key={response.id} className="space-y-1">
                          <div className="bg-blue-50 rounded-lg p-3">
                            <div className="mb-2">
                              <p className="text-sm font-medium text-blue-900 leading-tight">
                                {response.questions?.text || 'Question not found'}
                              </p>
                              {response.questions?.shortname && (
                                <p className="text-xs text-blue-600 mt-1 font-mono bg-blue-100 px-2 py-1 rounded inline-block">
                                  {response.questions.shortname}
                                </p>
                              )}
                            </div>
                            {response.response_text && (
                              <p className="text-sm text-blue-800 mt-2">
                                <span className="font-medium">Response:</span> {response.response_text}
                              </p>
                            )}
                            {response.response_options && response.response_options.length > 0 && (
                              <p className="text-sm text-blue-800 mt-2">
                                <span className="font-medium">Selected:</span> {response.response_options.join(', ')}
                              </p>
                            )}
                            <p className="text-xs text-blue-600 mt-3 pt-2 border-t border-blue-200">
                              {format(new Date(response.created_at), 'MMM dd, HH:mm:ss')}
                            </p>
                          </div>
                          {index < conversation.user_responses.length - 1 && (
                            <Separator className="my-2" />
                          )}
                        </div>
                      ))}
                        {conversation.user_responses.length === 0 && (
                          <p className="text-center text-muted-foreground py-4">
                            No responses found
                          </p>
                        )}
                      </div>
                    </ScrollArea>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Trigger History */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Target className="h-5 w-5 text-orange-600" />
                    <CardTitle className="text-lg">Trigger History</CardTitle>
                    <Badge variant="outline">{conversation.trigger_logs.length} events</Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <ScrollArea className="h-[350px] px-4">
                  <div className="space-y-3 py-2">
                    {conversation.trigger_logs.map((log, index) => {
                      // Determine status - use the status field first, then fallback to computed status
                      const dbStatus = log.status?.toLowerCase();
                      const isSuccess = dbStatus === 'success' || (log.response_status && log.response_status >= 200 && log.response_status < 300);
                      const isError = dbStatus === 'error' || log.error_message || (log.response_status && log.response_status >= 400);
                      const isPending = dbStatus === 'pending' || (!isSuccess && !isError);

                      // Get trigger name from multiple sources
                      const triggerName = log.trigger_name || log.trigger_rules?.name || 'Unknown Trigger';
                      const webhookUrl = log.webhook_url || log.trigger_rules?.webhookurl;

                      return (
                        <div key={log.id} className="space-y-1">
                          <div className={`rounded-lg p-3 border ${
                            isSuccess
                              ? 'bg-green-50 border-green-200'
                              : isError
                                ? 'bg-red-50 border-red-200'
                                : 'bg-gray-50 border-gray-200'
                          }`}>
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex items-center gap-2">
                                {isSuccess && <CheckCircle className="h-4 w-4 text-green-600" />}
                                {isError && <XCircle className="h-4 w-4 text-red-600" />}
                                {isPending && <Zap className="h-4 w-4 text-gray-600" />}
                                <span className="font-medium text-sm">
                                  {triggerName}
                                </span>
                                <Badge
                                  variant={isSuccess ? 'default' : isError ? 'destructive' : 'secondary'}
                                  className="text-xs"
                                >
                                  {log.status || (isSuccess ? 'success' : isError ? 'error' : 'pending')}
                                </Badge>
                                {log.execution_time_ms && (
                                  <Badge variant="outline" className="text-xs">
                                    {log.execution_time_ms}ms
                                  </Badge>
                                )}
                                {log.response_status && (
                                  <Badge variant="outline" className="text-xs">
                                    {log.response_status}
                                  </Badge>
                                )}
                              </div>
                              <span className="text-xs text-muted-foreground">
                                {format(new Date(log.executed_at), 'HH:mm:ss')}
                              </span>
                            </div>

                            {webhookUrl && (
                              <div className="text-xs text-muted-foreground mb-2">
                                <ExternalLink className="h-3 w-3 inline mr-1" />
                                {webhookUrl}
                              </div>
                            )}

                            {log.error_message && (
                              <div className="text-xs text-red-600 bg-red-100 rounded px-2 py-1 mb-2">
                                <strong>Error:</strong> {log.error_message}
                              </div>
                            )}

                            {log.payload && (
                              <details className="text-xs">
                                <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                                  View Payload ({Object.keys(log.payload).length} fields)
                                </summary>
                                <pre className="mt-2 p-2 bg-gray-100 rounded text-xs overflow-x-auto">
                                  {JSON.stringify(log.payload, null, 2)}
                                </pre>
                              </details>
                            )}

                            {(log.response_data || log.response) && (
                              <details className="text-xs mt-2">
                                <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                                  View Response {log.response_status && `(${log.response_status})`}
                                </summary>
                                <pre className="mt-2 p-2 bg-gray-100 rounded text-xs overflow-x-auto">
                                  {JSON.stringify(log.response_data || log.response, null, 2)}
                                </pre>
                              </details>
                            )}
                          </div>
                          {index < conversation.trigger_logs.length - 1 && (
                            <Separator className="my-2" />
                          )}
                        </div>
                      );
                    })}
                    {conversation.trigger_logs.length === 0 && (
                      <div className="text-center py-8 text-muted-foreground">
                        <Target className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">No triggers executed</p>
                        <p className="text-xs">Webhooks and triggers will appear here when executed</p>
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
                </div>
              </ScrollArea>
            </TabsContent>

            {/* Details Tab */}
            <TabsContent value="details" className="mt-6">
              <ScrollArea className="h-[calc(95vh-200px)]">
                <div className="space-y-4 pr-4">
              {/* Conversation Overview */}
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">Overview</CardTitle>
                    {getStatusBadge()}
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="font-medium">Started</p>
                        <p className="text-muted-foreground">
                          {format(new Date(conversation.created_at), 'MMM dd, yyyy HH:mm')}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <MessageSquare className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="font-medium">Messages</p>
                        <p className="text-muted-foreground">{conversation.messages.length}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="font-medium">Responses</p>
                        <p className="text-muted-foreground">{conversation.user_responses.length}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="font-medium">Duration</p>
                        <p className="text-muted-foreground">
                          {conversation.metadata?.response_time
                            ? `${conversation.metadata.response_time.toFixed(1)}s`
                            : 'N/A'
                          }
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Session Information */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Session Information</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    {Object.entries(formatMetadata(conversation.metadata)).map(([key, value]) => (
                      <div key={key} className="flex justify-between">
                        <span className="font-medium text-muted-foreground">{key}:</span>
                        <span className="text-right max-w-[200px] truncate" title={String(value)}>
                          {String(value)}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* AI Extracted Data */}
              {conversation.metadata?.ai_extracted_data && (
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Zap className="h-5 w-5 text-purple-600" />
                        AI Extracted Data
                      </CardTitle>
                      {conversation.metadata.extracted_at && (
                        <Badge variant="outline" className="bg-purple-50 text-purple-700">
                          {format(new Date(conversation.metadata.extracted_at), 'MMM d, h:mm a')}
                        </Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                      {Object.entries(conversation.metadata.ai_extracted_data)
                        .sort(([a], [b]) => a.localeCompare(b))
                        .map(([key, value]) => (
                        <div key={key} className="flex justify-between p-2 bg-purple-50 rounded">
                          <span className="font-medium text-purple-900 capitalize">{key.replace('_', ' ')}:</span>
                          <span className="text-right max-w-[200px] truncate text-purple-800" title={String(value)}>
                            {String(value)}
                          </span>
                        </div>
                      ))}
                    </div>
                    <div className="text-xs text-muted-foreground mt-3 pt-3 border-t">
                      <p>This data was automatically extracted from the AI conversation using intelligent field detection.</p>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* PII Data */}
              {piiToken && (
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <User className="h-5 w-5 text-blue-600" />
                        PII Data
                      </CardTitle>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="bg-blue-50 text-blue-700">
                          Token: {piiToken.substring(0, 8)}...
                        </Badge>
                        {piiData?._enhanced && (
                          <Badge variant="outline" className="bg-green-50 text-green-700">
                            Auto-Created
                          </Badge>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {isPiiLoading ? (
                      <div className="flex items-center justify-center py-4">
                        <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                        <span className="text-sm text-muted-foreground">Loading PII data...</span>
                      </div>
                    ) : piiData ? (
                      <div className="space-y-4">
                        {/* User Data */}
                        <div>
                          <h4 className="text-sm font-medium text-gray-900 mb-2">User Information</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                            {userData?.fields && Object.entries(userData.fields)
                              .sort(([a], [b]) => a.localeCompare(b))
                              .map(([key, value]) => (
                                <div key={key} className="flex justify-between p-2 bg-blue-50 rounded">
                                  <span className="font-medium text-blue-900 capitalize">{key}:</span>
                                  <span className="text-right max-w-[200px] truncate text-blue-800" title={String(value?.value)}>
                                    {String(value?.value)}
                                  </span>
                                </div>
                              ))}
                          </div>
                          {!userData?.fields && (
                            <div className="text-center py-3 text-muted-foreground bg-gray-50 rounded">
                              <p className="text-sm">No user data collected yet</p>
                              <p className="text-xs">Data will appear here as the user provides information</p>
                            </div>
                          )}
                        </div>

                        {/* Session Metadata */}
                        {piiData._session_id && (
                          <div>
                            <h4 className="text-sm font-medium text-gray-900 mb-2">Session Metadata</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                              {piiData._session_id && (
                                <div className="flex justify-between p-2 bg-gray-50 rounded">
                                  <span className="font-medium text-gray-700">Session ID:</span>
                                  <span className="text-right font-mono text-xs text-gray-600" title={String(piiData._session_id)}>
                                    {String(piiData._session_id).substring(0, 20)}...
                                  </span>
                                </div>
                              )}
                              {piiData._conversation_id && (
                                <div className="flex justify-between p-2 bg-gray-50 rounded">
                                  <span className="font-medium text-gray-700">Conversation ID:</span>
                                  <span className="text-right font-mono text-xs text-gray-600" title={String(piiData._conversation_id)}>
                                    {String(piiData._conversation_id).substring(0, 20)}...
                                  </span>
                                </div>
                              )}
                              {piiData._created_at && (
                                <div className="flex justify-between p-2 bg-gray-50 rounded">
                                  <span className="font-medium text-gray-700">Created:</span>
                                  <span className="text-right text-gray-600">
                                    {format(new Date(String(piiData._created_at)), 'MMM dd, HH:mm')}
                                  </span>
                                </div>
                              )}
                              {piiData._enhanced && (
                                <div className="flex justify-between p-2 bg-green-50 rounded">
                                  <span className="font-medium text-green-700">Type:</span>
                                  <span className="text-right text-green-600">Auto-Created PII</span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/*/!* Chat References *!/*/}
                        {/*{piiData._chat_references && Object.keys(piiData._chat_references).length > 0 && (*/}
                        {/*  <div>*/}
                        {/*    <h4 className="text-sm font-medium text-gray-900 mb-2">Chat References</h4>*/}
                        {/*    <div className="space-y-2">*/}
                        {/*      {Object.entries(piiData._chat_references).map(([chatbotId, chatData]: [string, any]) => (*/}
                        {/*        <div key={chatbotId} className="p-3 bg-purple-50 rounded border border-purple-200">*/}
                        {/*          <div className="flex justify-between items-start mb-2">*/}
                        {/*            <span className="font-medium text-purple-900 text-sm">*/}
                        {/*              Chatbot: {chatbotId.substring(0, 8)}...*/}
                        {/*            </span>*/}
                        {/*            {chatData.updated_at && (*/}
                        {/*              <span className="text-xs text-purple-600">*/}
                        {/*                {format(new Date(chatData.updated_at), 'MMM dd, HH:mm')}*/}
                        {/*              </span>*/}
                        {/*            )}*/}
                        {/*          </div>*/}
                        {/*          {chatData.question_answers && Object.keys(chatData.question_answers).length > 0 && (*/}
                        {/*            <div className="text-xs text-purple-700">*/}
                        {/*              {Object.keys(chatData.question_answers).length} responses saved*/}
                        {/*            </div>*/}
                        {/*          )}*/}
                        {/*        </div>*/}
                        {/*      ))}*/}
                        {/*    </div>*/}
                        {/*  </div>*/}
                        {/*)}*/}

                        <div className="text-xs text-muted-foreground mt-3 pt-3 border-t">
                          <p>
                            {piiData._enhanced
                              ? "This PII record was automatically created for this chat session. User responses and session data are preserved for continuity."
                              : "This PII data was associated with the conversation session and can be used for variable replacement in messages."
                            }
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-4 text-muted-foreground">
                        <User className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">Failed to load PII data</p>
                        <p className="text-xs">The PII token may be invalid or expired</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
                </div>
              </ScrollArea>
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
};
